import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { resetPassword, signIn } from '@aws-amplify/auth';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthStoreService, DeliveryMedium } from '../../store/auth';
import { UserStoreService } from '../../store/user';
import { emailValidator } from '@railmybox/shared/util';
import { TranslocoService } from '@jsverse/transloco';

@UntilDestroy()
@Component({
  selector: 'rmb-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  private authStoreService = inject(AuthStoreService);
  private userStoreService = inject(UserStoreService);
  private translocoService = inject(TranslocoService);

  readonly TRANSLOCO_PREFIX = 'auth.login.form';
  loginForm: FormGroup;
  errorMessage: string;
  confirmationModal = false;
  confirmEmailForm: FormGroup;
  readonly loading = signal(false);
  readonly loadingLogin = signal(false);

  constructor() {
    this.loginForm = this.getLoginForm();
  }

  ngOnInit() {
    this.confirmEmailForm = this.formBuilder.group({
      email: ['', [Validators.required, emailValidator]],
    });
  }

  /**
   * Logs the user in app
   *
   * In case of an error update the view
   */
  async login(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }
    this.errorMessage = '';
    this.loading.set(true);
    this.loadingLogin.set(true);
    const { email, password } = this.loginForm.value;
    this.authStoreService.updateUserCredentials({ email });
    this.userStoreService.updateUserState({ email });

    try {
      const user = await signIn({ username: email, password });
      if (user.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        await this.router.navigate(['/auth', 'reset-password']);
      } else if (user.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        await this.router.navigate(['/auth', 'confirm-login']);
      }
      await this.updateUserSessionData();
    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        await this.router.navigate(['/auth', 'verify']);
      }
      if (err.name === 'PasswordResetRequiredException') {
        await this.router.navigate(['/auth', 'reset-password']);
      }
      if (err.name === 'UserAlreadyAuthenticatedException') {
        await this.updateUserSessionData();
      }
      this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
    } finally {
      this.loading.set(false);
      this.loadingLogin.set(false);
    }
  }
  async updateUserSessionData(): Promise<void> {
    const attributes = await this.authStoreService.updateSessionState();
    this.userStoreService.updateUserAttributes(attributes);
    await this.authStoreService.redirectAfterLogin(this.router, this.userStoreService.getUserQuery().getInvitationKey());
  }

  /**
   * If user entered valid e-mail into login form email control field, it is filled into send reset code form
   */
  resetPasswordModal() {
    const email = this.loginForm.get('email');

    if (email.valid) {
      this.confirmEmailForm.patchValue({ email: email.value });
    }
    this.toggleConfirmModal();
  }

  /**
   * E-mail confirm modal switch
   */
  toggleConfirmModal() {
    this.confirmationModal = !this.confirmationModal;
  }

  /**
   * Navigates the user to reset password page
   */
  async onResetPassword(): Promise<void> {
    const email = this.confirmEmailForm.get('email');
    if (email.valid) {
      this.loading.set(true);
      this.authStoreService.updateUserCredentials({
        email: email.value,
      });
      return await this.sendVerificationCode();
    }
  }

  /**
   * Makes an API call to send verification code (Only is email is valid)
   */
  async sendVerificationCode(): Promise<void> {
    try {
      this.authStoreService.updateUserCredentials({
        email: this.confirmEmailForm.get('email').value,
      });
      const resetPasswordResult = await resetPassword({ username: this.confirmEmailForm.get('email').value });
      const { deliveryMedium, destination } = resetPasswordResult.nextStep.codeDeliveryDetails;
      this.authStoreService.updateUIState({
        deliveryMedium: deliveryMedium === 'EMAIL' ? DeliveryMedium.EMAIL : null,
        destination: destination,
      });
      this.loading.set(false);
      await this.router.navigate(['/auth', 'reset-password']);
    } catch (err) {
      this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
    }
  }

  /**
   * Retrieves the login form
   * @private
   */
  private getLoginForm(): FormGroup {
    return this.formBuilder.group({
      email: ['', [Validators.required, emailValidator]],
      password: ['', [Validators.required]],
    });
  }
}
